import { useState, useEffect } from "react";
import { BrowserRouter as Router, Route, Switch, Redirect } from "react-router-dom";
import axios from "axios";
import * as Sentry from "@sentry/react";
import "./style.css";

// Global API timeout — safety net for truly hung requests only.
// Per-context timeouts are set in sync/payment flows (60s/120s).
// This catches anything that slips through without a specific timeout.
axios.defaults.timeout = 60000;

// UI Watchdog — detects frozen UI and auto-reloads after 15s
import { startWatchdog } from "../utils/watchdog";
// Health Reporter — collects metrics, computes health score, reports every 5min
import { startHealthReporter } from "../utils/healthReporter";
startWatchdog();
startHealthReporter();
import ErrorBoundary from "./ErrorBoundary";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { EventBusProvider } from "../lib/components/EventBusProvider";
import OrderNotification from "./OrderNotification";
import SignIn from "./Authentication";
import tenantConfig from "../config/tenantConfig";
import { initTenantStorage } from "../config/tenantStorage";
import useAuthStore from "../stores/useAuthStore";
import usePOSConfigStore from "../stores/usePOSConfigStore";
import useCartStore from "../stores/useCartStore";
import useSessionStore from "../stores/useSessionStore";
import useTransactionStore from "../stores/useTransactionStore";
import useRestaurantStore from "../stores/useRestaurantStore";
import useReferenceStore from "../stores/useReferenceStore";
import useAdvanceOrderStore from "../stores/useAdvanceOrderStore";
import PrivateRoute from "./PrivateRoute";
import NavigationBlocker from "../NavigationBlocker";

import TillModalHandler from "./TillModalHandler";
import SupportButton from "./Support/SupportButton";
import CrashRecoveryProvider from "./CrashRecoveryProvider";
import { installPrintInterceptor } from "../lib/printer/printInterceptor";
import { checkAppVersionGate, checkSchemaVersionGate, stampCurrentVersion } from "../utils/appVersionGate";

// POS routes
import PointOfSale from "./PointOfSale";
import DualScreenPOS from "./dualScreen";
import Till from "./Till/Till";
import Login from "./Login";
import Sync from "./Sync";
import OpenTill from "./OpenTill";
import CloseTill from "./CloseTill";
import CloseTillAll from "./CloseTill/CloseTIllAll";
import CashToKeep from "./CloseTill/cashToKeep";
import OpenTillComponent from "./OpenTill/OpenTillComponent";
import Dashboard2 from "./Dashboard2";
import ProductionOrder from "./ProductionOrder";
import ProductionRMIssue from "./ProductionRMIssue";
import PurchaseOrder from "./PurchaseOrder";
import Inventory from "./Inventory";
import Aggregator from "./Aggregator";
import CallCenter from "./CallCenter";
import Estimate from "./Estimate";
import Preorder from "./PreOrder";
import RestaurantPreorder from "./PointOfSale/Restaurant/restaurantPreorder";
import TableManagement from "./PointOfSale/Restaurant/Tables-Management/tables";
import HealthMonitor from "./HealthMonitor";
import { WebSocketProvider, useWebSocket } from "../lib/components/WebSocketProvider";
import { getSalesHistoryDetails } from "../utils/salesHistoryUtils";

// Auth-aware redirect: /till if has auth cookies, /sign-in if not
function AuthRedirect() {
  const hasAuth = document.cookie.includes("accessToken=");
  console.debug("[AUTH-DIAG] AuthRedirect", { hasAuth, redirectTo: hasAuth ? "/till" : "/sign-in" });
  return <Redirect to={hasAuth ? "/till" : "/sign-in"} />;
}

const App = () => {
  const SentryDSN = process.env.REACT_APP_SENTRY_DSN;
  const SentryEnabled = process.env.REACT_APP_ENABLE_SENTRY;
  const environment = process.env.REACT_APP_Sentry_Environment;
  const [tenantReady, setTenantReady] = useState(false);

  const setTokens = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const searchKey = searchParams.get("searchKey");
    const nameTill = searchParams.get("nameTill");
    if (searchKey) {
      localStorage.setItem("tiilId", searchKey);
      localStorage.setItem("tillName", nameTill);
    }
  };

  useEffect(() => {
    let userPreferences = JSON.parse(localStorage.getItem("userPreferences"));
    if (!userPreferences?.dateFormat) {
      userPreferences = {
        dateFormat: "DD-MM-YYYY",
        dateTimeFormat: "DD-MM-YYYY HH:mm:ss",
      };
      localStorage.setItem("userPreferences", JSON.stringify(userPreferences));
    }
    setTokens();

    // Install print interceptor for Electron embedded printing
    if (window.electronAPI) {
      installPrintInterceptor();
    }

    const initTenant = async () => {
      try {
        // Version gate: clear stale localStorage when app version or schema changes
        const versionChanged = checkAppVersionGate();
        const schemaChanged = !versionChanged && checkSchemaVersionGate();
        if (versionChanged || schemaChanged) {
          console.debug("[App] Version/schema changed — reloading with fresh state");
          window.location.reload();
          return;
        }
        stampCurrentVersion();

        const activeTenant = await tenantConfig.initialize();
        console.debug("[AUTH-DIAG] App.initTenant", {
          activeTenant: activeTenant ? { code: activeTenant.code, name: activeTenant.name } : null,
          isElectron: !!window.electronAPI,
          hasTenantAPI: !!window.electronAPI?.tenant,
          url: window.location.href,
        });
        if (activeTenant) {
          initTenantStorage(activeTenant.code);
        }

        try {
          useAuthStore.getState().rehydrateFromStorage();
        } catch (e) {
          console.error("[App] Auth rehydrate failed:", e);
        }

        const tillDataAfterRehydrate = useAuthStore.getState().tillData;
        console.debug("[AUTH-DIAG] After auth rehydrate", {
          hasTillData: !!tillDataAfterRehydrate,
          hasTillAccess: !!tillDataAfterRehydrate?.tillAccess,
        });

        const results = await Promise.allSettled([
          usePOSConfigStore.persist.rehydrate(),
          useCartStore.persist.rehydrate(),
          useSessionStore.persist.rehydrate(),
          useTransactionStore.persist.rehydrate(),
          useRestaurantStore.persist.rehydrate(),
          useReferenceStore.persist.rehydrate(),
          useAdvanceOrderStore.persist.rehydrate(),
        ]);
        const failed = results.filter((r) => r.status === "rejected");
        if (failed.length > 0) {
          console.error(
            "[App] Store rehydration failures:",
            failed.map((r) => r.reason)
          );
        }
        setTenantReady(true);
      } catch (err) {
        console.error("[App] Tenant init failed:", err);
        setTenantReady(true);
      }
    };
    initTenant();
  }, []);

  useEffect(() => {
    if (SentryEnabled) {
      Sentry.init({
        environment: environment,
        dsn: SentryDSN,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],
        tracesSampleRate: 1.0,
        ignoreErrors: [
          "ResizeObserver loop limit exceeded",
          "ResizeObserver loop completed with undelivered notifications",
          "ResizeObserver loop completed with undelivered notifications.",
        ],
      });
    }
  }, []);

  const wsUrl = process.env.REACT_APP_WS_URL;

  // Wait for tenant to load before rendering the app
  if (!tenantReady) {
    return (
      <div className="aspectRatio" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p>Loading...</p>
      </div>
    );
  }

  return <AppShell wsUrl={wsUrl} />;
};

const AppShell = ({ wsUrl }) => {
  const tillData = useAuthStore((s) => s.tillData);
  const bunitId = tillData?.tillAccess?.csBunit?.csBunitId || null;
  const tillId = tillData?.tillAccess?.cwrTill?.till || null;

  return (
    <div className="aspectRatio" style={{ display: "flex", flexDirection: "column" }}>
      <CrashRecoveryProvider getSessionState={() => ({})} onRestore={() => {}}>
        <I18nextProvider i18n={i18n}>
          <EventBusProvider>
            <WebSocketProvider url={wsUrl} bunitId={bunitId} tillId={tillId} enabled={!!bunitId} debug={true}>
              <AppContent />
            </WebSocketProvider>
          </EventBusProvider>
        </I18nextProvider>
      </CrashRecoveryProvider>
      <SupportButton />
    </div>
  );
};

const AppContent = () => {
  const { visibleOrders, lastMessage, removeVisibleOrder, removeVisibleOrderByExternalId } = useWebSocket();

  const handleAutoAccept = async (orderData) => {
    removeVisibleOrder(orderData?.data?.orderId);
  };

  const handleOrderUpdate = async (data) => {
    try {
      if (data?.new_state === "Acknowledged") {
        const externalOrderId = data?.additional_info?.external_channel?.order_id;
        console.debug("Handling order:update for external order ID:", externalOrderId);
        const orderDetails = await getSalesHistoryDetails(externalOrderId, "AK", true, data?.order_id);

        if (!orderDetails) {
          console.error(`No order details for external order ID: ${externalOrderId}`);

          return;
        }

        removeVisibleOrderByExternalId(externalOrderId);
      }
    } catch (error) {
      console.error("Error handling websocket order:update:", error);
    }
  };

  useEffect(() => {
    if (!lastMessage) return;

    const eventType = lastMessage?.type || lastMessage?.event;

    if (eventType === "order:update") {
      handleOrderUpdate(lastMessage?.data);
    }
  }, [lastMessage]);

  const handleReject = (orderData) => {
    removeVisibleOrder(orderData?.data?.orderId);
  };

  const handleOnClose = (orderData) => {
    removeVisibleOrder(orderData?.data?.orderId);
  };

  return (
    <Router>
      <ErrorBoundary>
        <NavigationBlocker />
        <TillModalHandler />
        {visibleOrders.map(
          (order, index) => (
            // window.location.pathname !== "/aggregator" ? (
            <OrderNotification
              key={order.data.orderId}
              orderData={order}
              index={index}
              onAutoAccept={(options) => handleAutoAccept(order, options)}
              onReject={() => handleReject(order)}
              onClose={() => handleOnClose(order)}
            />
          )
          // ) : null
        )}
        <Switch>
          {/* Public routes */}
          <Route exact path="/sign-in" component={SignIn} />
          <Route exact path="/health" component={HealthMonitor} />
          <Route exact path="/till" component={Till} />
          <Route exact path="/login" component={Login} />

          {/* Private POS routes */}
          <PrivateRoute exact path="/pos" component={PointOfSale} />
          <PrivateRoute exact path="/dualscreen" component={DualScreenPOS} />
          <PrivateRoute exact path="/sync" component={Sync} />
          <PrivateRoute exact path="/dashboard2" component={Dashboard2} errorBoundary="Dashboard" />
          <PrivateRoute exact path="/production" component={ProductionOrder} errorBoundary="Production" />
          <PrivateRoute exact path="/production-rm-issue" component={ProductionRMIssue} errorBoundary="Production RM" />
          <PrivateRoute exact path="/purchaseOrder" component={PurchaseOrder} errorBoundary="Purchase Order" />
          <PrivateRoute exact path="/inventory/stock-indent" component={Inventory} errorBoundary="Inventory" />
          <PrivateRoute exact path="/aggregator" component={Aggregator} errorBoundary="Aggregator" />
          <PrivateRoute exact path="/estimate" component={Estimate} errorBoundary="Estimate" />
          <PrivateRoute exact path="/preorder" component={Preorder} errorBoundary="Preorder" />
          <PrivateRoute exact path="/restaurant/preorder" component={RestaurantPreorder} errorBoundary="Restaurant Preorder" />
          <PrivateRoute exact path="/open-till" component={OpenTill} />
          <PrivateRoute exact path="/cash-to-keep" component={CashToKeep} />
          <PrivateRoute exact path="/open-till-component" component={OpenTillComponent} />
          <PrivateRoute exact path="/close-till" component={CloseTill} />
          <PrivateRoute exact path="/close-till-all" component={CloseTillAll} />
          <PrivateRoute exact path="/table-management" component={TableManagement} errorBoundary="Table Management" />
          <PrivateRoute exact path="/call-center" component={CallCenter} errorBoundary="Call Center" />

          {/* Catch-all: redirect based on auth state */}
          <Route component={AuthRedirect} />
        </Switch>
      </ErrorBoundary>
    </Router>
  );
};

export default App;
